import React, { useState, useEffect, useRef } from "react";
import '../styles/ActivityFeed.css';
import {
    StickyNote,
    Mail as Email,
    LucideScrollText as All,
    Printer,
    Phone,
    MessageSquare,
    CheckSquare,
    AlarmClock,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    Ellipsis,
    Pin,
    PinOff,
    CheckCheck,
    Minimize2,
    Maximize2,
    Download,
} from "lucide-react";
import { SearchBar } from "./Nav";
import { AddActivity, DateInput, File, MultiFile, Notification, SearchSelect } from "./FieldComponents";
import { useNavigate } from "react-router";
import { useOptimizedResize } from '../utils/performance';
import fuzzysort from 'fuzzysort';
import { formatLastModified } from "./NestedFeed";
import { formatCommunicationStatus } from '../utils/communicationStatus';

const containsHtmlTags = (content = '') => /<\/?[a-z][\s\S]*>/i.test(String(content));

const escapeHtml = (content = '') => String(content)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const normalizeEmailNewlines = (content = '') => String(content)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n');

const formatEmailContentForDisplay = (content = '') => {
    const normalizedContent = normalizeEmailNewlines(content);
    const isHtml = containsHtmlTags(normalizedContent);

    return {
        html: isHtml ? normalizedContent : escapeHtml(normalizedContent),
        preserveWhitespace: !isHtml,
    };
};

const iconMap = {
    All: <All size={18} />,
    Notes: <StickyNote size={18} />,
    Emails: <Email size={18} />,
    Faxes: <Printer size={18} />,
    'Phone Calls': <Phone size={18} />,
    Texts: <MessageSquare size={18} />,
    Tasks: <CheckSquare size={18} />,
    Reminders: <AlarmClock size={18} />,
};

const FeedItem = ({ type, setActiveFeed, activeFeed, notifications, mobile }) => {
    let feedType = 'All';
    switch (type) {
        case 'Phone Calls':
            feedType = 'calls';
            break;
        case 'Faxes':
            feedType = 'faxes';
            break;
        default:
            feedType = type;
            break;
    }
    return (
        <div 
            className={`action ${activeFeed === feedType ? 'tertiary' : 'quaternary'} feed-type`} 
            style={ activeFeed === feedType ? { backgroundColor: 'var(--secondary-color)', fontSize: '12.8px' } : { backgroundColor: 'var(--secondary-color)', color: 'var(--subtext-color)', fontSize: '12.8px' }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--hover-color)';

                if (activeFeed !== feedType) {
                    e.currentTarget.style.color = 'var(--text-color) !important';
                }
            }}
            onMouseLeave={(e) => { 
                e.currentTarget.style.borderColor = 'var(--border-color)';

                if(activeFeed !== feedType || e.currentTarget.style.color === 'var(--text-color)') {
                    e.currentTarget.style.color = 'var(--subtext-color)'
                }
            }}
            onClick={() => setActiveFeed(feedType)}
        >
            <span className='feed-type-text'>
                {iconMap[type]}{!mobile && type}
                {!mobile && type === 'Reminders' && (notifications.reminder > 0 || notifications.priority > 0) &&
                    <div className='tasks-notis'>
                        {notifications.priority > 0 && <Notification count={notifications.priority} type='priority' />}
                        {notifications.reminder > 0 && <Notification count={notifications.reminder} type='reminder' />}
                    </div>
                }
            </span>
        </div>
    );
};

// Helper to determine task completion status
const getTaskCompletionStatus = (task, currentUserId) => {
    if (!task.deadline?.assignees) return { status: 'incomplete', userCompleted: false };
    
    const assignees = task.deadline.assignees;
    
    // Handle completion_status as either object or JSON string
    let completionStatus = task.deadline.completion_status || {};
    if (typeof completionStatus === 'string') {
        try {
            completionStatus = JSON.parse(completionStatus);
        } catch (e) {
            completionStatus = {};
        }
    }
    
    if (assignees.length === 0) {
        // Legacy single-assignee task
        return {
            status: task.deadline.done ? 'complete' : 'incomplete',
            userCompleted: !!task.deadline.done
        };
    }
    
    // Check how many assignees have completed
    let completedCount = 0;
    let userCompleted = false;
    
    for (const assigneeId of assignees) {
        if (completionStatus[assigneeId]) {
            completedCount++;
        }
        if (assigneeId === currentUserId && completionStatus[assigneeId]) {
            userCompleted = true;
        }
    }
    
    let status = 'incomplete';
    if (completedCount === assignees.length) {
        status = 'complete';
    } else if (completedCount > 0) {
        status = 'partial';
    }
    
    return { status, userCompleted, completedCount, totalCount: assignees.length };
};

const Activity = ({ data, user_id, user, fetchFeed, reminder, priority, setActiveItem, activeItem, users, lead_id, isReply = false }) => {
    const [showOptions, setShowOptions] = useState(false);
    const optionRef = useRef(null);
    const [hoveringPin, setHoveringPin] = useState(null);
    const contentRef = useRef(null);
    const [canExpand, setCanExpand] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isReplying, setIsReplying] = useState(false);
    const [replyData, setReplyData] = useState({
        content: '',
        attachments: []
    });
    const [editData, setEditData] = useState({
        subject: data?.subject || '',
        content: data?.content || '',
        type: data?.type || 'notes',
        task: data?.task || '',
        due_date: data?.deadline?.due ? data.deadline.due.split(' ')[0] : '',
        attachments: [],
        tags: (() => {
            if (Array.isArray(data?.tags)) {
                return data.tags.join(', ');
            }
            if (typeof data?.tags === 'string') {
                try {
                    const parsed = JSON.parse(data.tags);
                    return Array.isArray(parsed) ? parsed.join(', ') : '';
                } catch {
                    return data.tags;
                }
            }
            return '';
        })()
    });
    const communicationStatus = formatCommunicationStatus(data);
    const deadline = data?.deadline?.due
        ? new Date(data.deadline.due).toLocaleDateString()
        : "Not set";
    const navigate = useNavigate();

    const completeTask = async (complete = true) => {
        try {
            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: Number(data?.id),
                    done: complete === true
                }),
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const result = await response.json();

            if (result.success) {
                fetchFeed();
            } else {
                console.error('Failed to mark task as complete:', result.message);
            }
        } catch (err) {
            console.error('Error completing task:', err);
        }
    };

    const pinNote = (pin, action) => {
        try {
            fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: data.id,
                    pin: pin,
                    action: action,
                    lead_id: lead_id
                }),
            })
            .then((response) => response.json())
            .then((result) => {
                if (result.success) {
                    setShowOptions(false);
                    fetchFeed();
                } else {
                    console.error("Error pinning note:", result.message);
                }
            })
            .catch((error) => {
                console.error("Error pinning note:", error);
            });
        } catch (error) {
            console.error("Error pinning note:", error);
        }
    }

    const handleDelete = async () => {
        if (!data?.id) return;
        if (!window.confirm('Delete this activity? This cannot be undone.')) return;

        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: data.id,
                    action: 'delete'
                })
            });

            const result = await response.json();
            if (result.success) {
                setShowOptions(false);
                fetchFeed();
            } else {
                console.error('Failed to delete activity:', result.message || result.error);
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
        }
    };

    const handleExpandClick = (event) => {
        // Don't minimize if user is selecting text
        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
            return;
        }
        
        // Don't minimize if the click started as a text selection (double-click, etc.)
        if (event.detail > 1) {
            return;
        }
        
        // Don't minimize if user is clicking on interactive elements
        const target = event.target;
        if (target.closest('button, a, input, textarea, select, [contenteditable]')) {
            return;
        }
        
        if (optionRef.current && !optionRef.current.contains(event.target)) {
            setActiveItem(activeItem === data.id ? null : data.id);
        }
    }

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (optionRef.current && !optionRef.current.contains(e.target)) {
                setShowOptions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const pinData = typeof data.pinned === 'string' ? JSON.parse(data.pinned) : data.pinned;
    const isPinnedForUser = Array.isArray(pinData?.users) && pinData.users.includes(Number(user_id));
    const isPinnedForLead = Number(pinData?.lead) === 1;

    const formatContentWithMentions = (content, users) => {
        if (!content || !users || users.length === 0) return content;

        const mentionPattern = /@([a-zA-Z0-9_]+)/g;
        
        return content.replace(mentionPattern, (match, username) => {
            const mentionedUser = users.find(u => u.user === username);
            if (mentionedUser) {
                return `<span class="tag-mention">@${username}</span>`;
            }
            return match;
        });
    };

    const formattedContent = data.type === 'emails' 
        ? data.content
        : formatContentWithMentions(data.content, users);
    const emailContent = data.type === 'emails'
        ? formatEmailContentForDisplay(data.content)
        : { html: data.content, preserveWhitespace: false };

    useEffect(() => {
        if (contentRef.current && activeItem !== data.id) {
            const hasScroll = contentRef.current.scrollHeight > contentRef.current.clientHeight;
            setCanExpand(hasScroll);
        }
    }, [formattedContent, activeItem]);

    const handleEdit = async () => {
        try {
            const formData = new FormData();
            formData.append('id', Number(data?.id));
            formData.append('edit', 'true');
            formData.append('subject', editData.subject);
            formData.append('content', editData.content);
            formData.append('type', editData.type);
            
            formData.append(
                "tags",
                JSON.stringify(
                    editData.tags
                    .split(/[\s,]+/)
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0)
                )
            );
            
            if (editData.type === 'tasks') {
                formData.append('task', editData.task);
                formData.append('due_date', editData.due_date);
            }

            // Handle file attachments
            if (Array.isArray(editData.attachments)) {
                editData.attachments.forEach((file) => {
                    if (file && typeof file.name === "string" && typeof file.size === "number") {
                        formData.append("attachment[]", file);
                    }
                });
            }

            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const result = await response.json();

            if (result.success) {
                setIsEditing(false);
                setShowOptions(false);
                fetchFeed();
            } else {
                console.error('Failed to edit activity:', result.message);
            }
        } catch (err) {
            console.error('Error editing activity:', err);
        }
    };

    const handleCancelEdit = () => {
        setEditData({
            subject: data?.subject || '',
            content: data?.content || '',
            type: data?.type || 'notes',
            task: data?.task || '',
            due_date: data?.deadline?.due ? data.deadline.due.split(' ')[0] : '',
            attachments: [],
            tags: (() => {
                if (Array.isArray(data?.tags)) {
                    return data.tags.join(', ');
                }
                if (typeof data?.tags === 'string') {
                    try {
                        const parsed = JSON.parse(data.tags);
                        return Array.isArray(parsed) ? parsed.join(', ') : '';
                    } catch {
                        return data.tags;
                    }
                }
                return '';
            })()
        });
        setIsEditing(false);
    };

    const canEdit = ['notes', 'tasks', 'reminders'].includes(data.type) && (Number(data.author_id) === Number(user_id || 12) || Number(data.author_id) === 12);
    const canDelete = Number(data.author_id) === Number(user_id || 12) || Number(data.author_id) === 12;

    const handleReply = async () => {
        try {
            const formData = new FormData();
            formData.append('lead_id', lead_id || 0);
            formData.append('type', 'notes');
            formData.append('subject', `Re: ${data.subject}`);
            formData.append('content', replyData.content);
            formData.append('reply', data.id);
            
            // Handle file attachments
            if (Array.isArray(replyData.attachments)) {
                replyData.attachments.forEach((file) => {
                    if (file && typeof file.name === "string" && typeof file.size === "number") {
                        formData.append("attachment[]", file);
                    }
                });
            }

            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const result = await response.json();

            if (result.success) {
                setIsReplying(false);
                setReplyData({ content: '', attachments: [] });
                setShowOptions(false);
                fetchFeed();
            } else {
                console.error('Failed to reply to activity:', result.message);
            }
        } catch (err) {
            console.error('Error replying to activity:', err);
        }
    };

    const handleCancelReply = () => {
        setReplyData({ content: '', attachments: [] });
        setIsReplying(false);
    };

    return (
        <div 
            className={`activity-feed-item ${(isPinnedForUser || isPinnedForLead) ? 'pinned-user' : ''} ${isEditing ? 'editing' : ''} ${isReplying ? 'replying' : ''}`}
            onClick={!isEditing && !isReplying ? handleExpandClick : undefined}
        >
            <div className="activity-feed-item-header subtext">
                <span className="activity-feed-item-author">{data.author}</span>
                <span className="activity-feed-item-date">{formatLastModified(data.creation_date)[3]} {formatLastModified(data.creation_date)[2]}</span>
                {data?.call_log && (
                    <span className="activity-call-info">
                        {`${data.call_log.caller} spoke with ${data.call_log.contact_name} on ${new Date(data.call_log.date).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: '2-digit',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        }).replace(',', ' @')}`}
                    </span>
                )}
                {data.task && (() => {
                    const completionInfo = getTaskCompletionStatus(data, user_id);
                    let assigneeDisplay = '';
                    
                    // Build assignee display using assignee_users from backend
                    if (data.assignee_users && Array.isArray(data.assignee_users) && data.assignee_users.length > 0) {
                        const firstAssignee = data.assignee_users[0];
                        const completionStatusObj = data.deadline?.completion_status || {};
                        const isFirstCompleted = completionStatusObj[firstAssignee.id];
                        
                        if (data.assignee_users.length === 1) {
                            assigneeDisplay = (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {firstAssignee.name}
                                    {isFirstCompleted && <CheckCheck size={14} style={{ color: 'var(--success-color)' }} />}
                                </span>
                            );
                        } else {
                            assigneeDisplay = (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {firstAssignee.name}
                                    {isFirstCompleted && <CheckCheck size={14} style={{ color: 'var(--success-color)' }} />}
                                    <span style={{ marginLeft: '4px' }}>+ {data.assignee_users.length - 1} more</span>
                                </span>
                            );
                        }
                    } else if (user) {
                        assigneeDisplay = user;
                    }
                    
                    return (
                        <span className={`activity-feed-item-task ${reminder.filter(p => p.id === data.id).length > 0 ? 'distant-reminder' : ''} ${priority.filter(p => p.id === data.id).length > 0 ? 'priority-reminder' : ''}`}>
                            Assigned to: {assigneeDisplay} Due: {deadline}
                            {completionInfo.status === 'partial' && (
                                <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--warning-color)' }}>
                                    ({completionInfo.completedCount}/{completionInfo.totalCount} completed)
                                </span>
                            )}
                        </span>
                    );
                })()}
            </div>
            {(() => {
                let tagsArray = [];
                if (Array.isArray(data?.tags)) {
                    tagsArray = data.tags;
                } else if (typeof data?.tags === 'string') {
                    try {
                        tagsArray = JSON.parse(data.tags);
                        if (!Array.isArray(tagsArray)) tagsArray = [];
                    } catch (e) {
                        tagsArray = [];
                    }
                }
                
                return tagsArray.length > 0 && (
                    <div className='activity-feed-tags'>
                        {tagsArray.map((tag, index) => (
                            <span key={index} className="activity-feed-tag tag">#{tag}</span>
                        ))}
                    </div>
                );
            })()}
            {isEditing ? (
                <div className="activity-edit-form" onClick={(e) => e.stopPropagation()}>
                    <div className='form-group activity'>
                        <label className='subtext'>Subject<span className='required'>*</span></label>
                        <input
                            type="text"
                            value={editData.subject}
                            onChange={(e) => setEditData({...editData, subject: e.target.value})}
                            className="edit-subject-input"
                            placeholder="Subject"
                        />
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Content<span className='required'>*</span></label>
                        <textarea
                            value={editData.content}
                            onChange={(e) => setEditData({...editData, content: e.target.value})}
                            className="edit-content-textarea"
                            placeholder="Content"
                            rows={5}
                        />
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Type</label>
                        <select
                            value={editData.type}
                            onChange={(e) => setEditData({...editData, type: e.target.value})}
                            className="edit-type-select"
                        >
                            <option value="notes">Note</option>
                            <option value="tasks">Task</option>
                        </select>
                    </div>
                    <div className='form-group activity'>
                        <label className='subtext'>Tags</label>
                        <input
                            type="text"
                            value={editData.tags}
                            onChange={(e) => setEditData({...editData, tags: e.target.value})}
                            placeholder="Enter tags separated by commas"
                        />
                        <small className='subtext'>Separate multiple tags with commas</small>
                    </div>
                    {editData.type === 'tasks' && (
                        <div className='activity-task-info'>
                            <div className='form-group activity'>
                                <label className='subtext'>Assigned to</label>
                                <SearchSelect
                                    value={editData.task}
                                    onChange={(val) => setEditData({...editData, task: Number(val)})}
                                    options={users?.reduce((acc, user) => {
                                        acc[user.id] = user.name;
                                        return acc;
                                    }, {})}
                                    placeholder="Select a user..."
                                />
                            </div>
                            <div className='form-group activity'>
                                <label className='subtext'>Due date</label>
                                <DateInput
                                    value={editData.due_date}
                                    onChange={(val) => setEditData({...editData, due_date: val})}
                                />
                            </div>
                        </div>
                    )}
                    <div className='form-group activity'>
                        <label className='subtext'>Attachments</label>
                        <MultiFile
                            value={editData.attachments}
                            onChange={(val) => setEditData({...editData, attachments: val})}
                        />
                    </div>
                    <div className="edit-actions">
                        <button onClick={handleEdit} className="action">Save</button>
                        <button onClick={handleCancelEdit} className="action alt">Cancel</button>
                    </div>
                </div>
            ) : isReplying ? (
                <>
                    <div className='activity-title'>
                        {data?.subject}
                    </div>
                    {communicationStatus && (
                        <div className={`activity-communication-status subtext ${communicationStatus.state}`}>
                            {communicationStatus.text}
                        </div>
                    )}
                    <div
                        className={`activity-feed-item-content ${activeItem === data.id ? 'expanded-activity' : ''}`}
                        ref={contentRef}
                        style={(data.type === 'emails' || data.type === '') ? { whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal", wordWrap: "break-word", maxWidth: "100%", overflowWrap: "break-word", overflowX: "hidden", textWrap: "wrap" } : {}}
                    >
                        {data?.type === 'emails' && data?.tags?.from && (
                            <div className="email-meta">
                                <p><strong>From:</strong> {data?.tags?.from}</p>
                                <p><strong>To:</strong> {data?.tags?.to}</p>
                                {data?.tags?.cc && <p><strong>CC:</strong> {data?.tags?.cc}</p>}
                                <br/>
                            </div>
                        )}
                        {data.type === 'emails' ? (
                            <div className='email-content-container' dangerouslySetInnerHTML={{ __html: emailContent.html }} style={{ whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal" }} />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: formattedContent }} />
                        )}
                    </div>
                    <div className="activity-reply-form" onClick={(e) => e.stopPropagation()}>
                        <div className='form-group activity'>
                            <label className='subtext'>Reply<span className='required'>*</span></label>
                            <textarea
                                value={replyData.content}
                                onChange={(e) => setReplyData({...replyData, content: e.target.value})}
                                className="reply-content-textarea"
                                placeholder="Enter your reply..."
                                rows={4}
                            />
                        </div>
                        <div className='form-group activity'>
                            <label className='subtext'>Attachments</label>
                            <MultiFile
                                value={replyData.attachments}
                                onChange={(val) => setReplyData({...replyData, attachments: val})}
                            />
                        </div>
                        <div className="reply-actions">
                            <button onClick={handleReply} className="action">Reply</button>
                            <button onClick={handleCancelReply} className="action alt">Cancel</button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className='activity-title'>
                        {data?.subject}
                    </div>
                    {communicationStatus && (
                        <div className={`activity-communication-status subtext ${communicationStatus.state}`}>
                            {communicationStatus.text}
                        </div>
                    )}
                    <div
                        className={`activity-feed-item-content ${activeItem === data.id ? 'expanded-activity' : ''}`}
                        ref={contentRef}
                        style={(data.type === 'emails' || data.type === '') ? { whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal", wordWrap: "break-word", maxWidth: "100%", overflowWrap: "break-word", overflowX: "hidden", textWrap: "wrap" } : {}}
                    >
                        {data?.type === 'emails' && data?.tags?.from && (
                            <div className="email-meta">
                                <p><strong>From:</strong> {data?.tags?.from}</p>
                                <p><strong>To:</strong> {data?.tags?.to}</p>
                                {data?.tags?.cc && <p><strong>CC:</strong> {data?.tags?.cc}</p>}
                                <br/>
                            </div>
                        )}
                        {data.type === 'emails' ? (
                            <div className='email-content-container' dangerouslySetInnerHTML={{ __html: emailContent.html }} style={{ whiteSpace: emailContent.preserveWhitespace ? "pre-wrap" : "normal" }} />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: formattedContent }} />
                        )}
                    </div>
                </>
            )}
            {Array.isArray(data.attachments) && <div className='files'>
                {data.attachments.slice(0, !hasOverflow ? 5 : data.attachments.length).map((file, index) => (
                    <File file={file} key={index} onClick={() => navigate(`${window.location.pathname}?section=3&folder=${data.type !== "calls" ? data.type === 'emails' ? 'Emails' : 'Notes' : 'Call Log'}`)}/>
                ))}
                {data.attachments.length > 5 && (
                    <div className='file small-text' onClick={(e) => {setHasOverflow(!hasOverflow); e.stopPropagation();}}>
                        {hasOverflow ? `show less` : `+${data.attachments.length - 5} more`}
                    </div>
                )}
            </div>}
            
            {/* Nested replies section */}
            {data.replies && data.replies.length > 0 && (
                <div className="activity-replies" onClick={(e) => e.stopPropagation()}>
                    {data.replies.map((reply) => (
                        <div key={`reply-${reply.id}`} className="activity-reply" onClick={(e) => e.stopPropagation()}>
                            <Activity
                                data={reply}
                                user_id={user_id}
                                user={users.find((user) => String(user.id) === String(reply.task))?.name || "Unknown"}
                                fetchFeed={fetchFeed}
                                reminder={reminder}
                                priority={priority}
                                setActiveItem={setActiveItem}
                                activeItem={activeItem}
                                users={users}
                                lead_id={lead_id}
                                isReply={false} // Allow replies to be replyable
                            />
                        </div>
                    ))}
                </div>
            )}
            
            <div className='activity-feed-item-type subtext'>
                {isEditing && <span className='subtext'>Editing...</span>}
                {canExpand && (
                    <div className="expand-toggle" onClick={handleExpandClick}>
                        {activeItem === data.id ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                    </div>
                )}
                {iconMap[data.type.charAt(0).toUpperCase() + data.type.slice(1)]}
            </div>
            <div className='activity-feed-item-menu' ref={optionRef}>
                <div className='activity-menu-actions'>
                    {data?.deadline?.done && 
                        <div className='task-complete subtext' onClick={() => completeTask(false)}>
                            complete<CheckCheck size={12} />
                        </div>
                    }
                    {data.pinned && (isPinnedForUser || isPinnedForLead) && (
                        <div className="activity-feed-item-pin">
                            <div
                                className='activity-pin'
                                title={isPinnedForLead ? 'Pinned to Lead Feed' : 'Pinned to My Feed'}
                                onMouseOver={() => setHoveringPin("personal")}
                                onMouseLeave={() => setHoveringPin(null)}
                            >
                                {hoveringPin !== 'personal'
                                    ? <Pin size={17} />
                                    : <PinOff size={17} onClick={() => pinNote(user_id, "remove")} />}
                            </div>
                        </div>
                    )}
                    <Ellipsis className="activity-feed-item-options" size={20} onClick={() => setShowOptions((prev) => !prev) }/>
                </div>
                {showOptions && (
                    <div className="activity-feed-item-options-menu">
                        {canEdit && !isEditing && <div className="activity-feed-item-option" onClick={() => { setIsEditing(true); setShowOptions(false); }}>Edit</div>}
                        <div className="activity-feed-item-option" onClick={() => { setIsReplying(true); setShowOptions(false); }}>Reply</div>
                        <div 
                            className="activity-feed-item-option" 
                            onClick={() => !isPinnedForUser ? pinNote(user_id) : pinNote(user_id, 'remove')}
                        >
                            {isPinnedForUser ? 'Unpin from my feed' : 'Pin to My Feed'}
                        </div>
                        {canDelete && (
                            <div className="activity-feed-item-option" onClick={handleDelete}>
                                Delete
                            </div>
                        )}
                        {data?.deadline?.assignees?.length > 0 && <div className="activity-feed-item-option" onClick={() => completeTask(data?.deadline?.done === null)}>{data?.deadline?.done ? 'Mark task incomplete' : 'Mark task complete'}</div>}
                    </div>
                )}
            </div>
        </div>
    )
}

export const LeadActivity = ({ lead_id, user_id }) => {
    const [feed, setFeed] = useState([]);
    const [activeFeed, setActiveFeed] = useState("All");
    const [expanded, setExpanded] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [addActivity, setAddActivity] = useState(false);
    const [users, setUsers] = useState([]);
    const [mobile, setMobile] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    
    // Scroll buttons state
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);
    const feedTypesRef = useRef(null);

    const reminder = feed?.filter((data) => (
        data.deadline?.due &&
        data.deadline?.done === null &&
        Array.isArray(data.deadline.assignees) &&
        data.deadline.assignees.map(Number).includes(Number(user_id)) &&
        new Date(data.deadline.due) > new Date().setDate(new Date().getDate() + 7)
    ));

    const priority = feed?.filter((data) => (
        data.deadline?.due &&
        data.deadline?.done === null &&
        Array.isArray(data.deadline.assignees) &&
        data.deadline.assignees.map(Number).includes(Number(user_id)) &&
        new Date(data.deadline.due) <= new Date().setDate(new Date().getDate() + 7)
    ));

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setMobile(true);
            } else {
                setMobile(false);
            }
        };
        handleResize();
    }, []);

    useOptimizedResize(() => {
        setMobile(window.innerWidth <= 768);
    });

    const today = new Date().toDateString();

    const fetchFeed = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/activity_feed.php?lead_id=${lead_id}&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            setFeed(data.feed_updates);
        } catch (error) {
            console.error("Error fetching activity feed:", error);
        }
    }
    
    const fetchUsers = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/user.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (response.ok) {
                const processedUsers = data.users.map(user => ({
                    ...user,
                    user: user.user || user.name.toLowerCase().replace(/\s+/g, '_')
                }));
                setUsers(processedUsers);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }

    const exportToPDF = async () => {
        try {
            const url = `https://api.casedb.co/activity_feed.php?lead_id=${lead_id}&export_pdf=true&feed_type=${encodeURIComponent(activeFeed)}&time=${new Date().getTime()}`;
            
            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `Activity_Feed_${activeFeed}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error exporting to PDF:", error);
            alert("Failed to export PDF. Please try again.");
        }
    }

    useEffect(() => {
        fetchFeed();
        fetchUsers();
    }, []);

    const checkScrollButtons = () => {
        if (feedTypesRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = feedTypesRef.current;
            setShowLeftScroll(scrollLeft > 0);
            setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    // Handle scroll button clicks
    const handleScrollLeft = () => {
        if (feedTypesRef.current) {
            feedTypesRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const handleScrollRight = () => {
        if (feedTypesRef.current) {
            feedTypesRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    // Check scroll buttons on mount and when window resizes
    useEffect(() => {
        checkScrollButtons();
        window.addEventListener('resize', checkScrollButtons);
        return () => window.removeEventListener('resize', checkScrollButtons);
    }, []);

    // Also check when mobile state changes
    useEffect(() => {
        checkScrollButtons();
    }, [mobile]);

    const filteredFeed = feed?.filter((item) => {
        const matchesType = (
            item.type === String(activeFeed).toLowerCase() ||
            activeFeed === "All" ||
            (item.type === "tasks" && activeFeed === "Reminders")
        );
    
        const matchesSearch = searchQuery.trim() === '' || (() => {
            const combined = `${item.subject ?? ''} ${item.content ?? ''}`;
            const result = fuzzysort.single(searchQuery, combined);
            return result !== null;
        })();
    
        return matchesType && matchesSearch && item !== "No activity found" && item.deleted !== 1;
    });

    // Organize replies - support nested replies
    const organizeReplies = (feedItems) => {
        if (!feedItems) return [];
        
        const itemsMap = new Map();
        const topLevelItems = [];
        
        // First pass: create a map of all items and initialize replies array
        feedItems.forEach(item => {
            itemsMap.set(item.id, { ...item, replies: [] });
        });
        
        // Second pass: organize into hierarchy
        feedItems.forEach(item => {
            if (!item.reply) {
                // Top-level item
                topLevelItems.push(itemsMap.get(item.id));
            } else {
                // This is a reply - attach to parent
                const parent = itemsMap.get(item.reply);
                if (parent) {
                    parent.replies.push(itemsMap.get(item.id));
                }
            }
        });
        
        // Sort function for replies by ID (ascending order)
        const sortRepliesById = (items) => {
            items.forEach(item => {
                if (item.replies && item.replies.length > 0) {
                    item.replies.sort((a, b) => a.id - b.id);
                    sortRepliesById(item.replies); // Recursively sort nested replies
                }
            });
        };
        
        // Sort all replies by ID
        sortRepliesById(topLevelItems);
        
        // Sort top level items by creation date (newest first)
        return topLevelItems.sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date));
    };

    const organizedFeed = organizeReplies(filteredFeed);

    const pinnedFeed = organizedFeed?.filter((item) => {
        if (!item.pinned) return false;
        const pinData = typeof item.pinned === 'string' ? JSON.parse(item.pinned) : item.pinned;
        const isPinnedForUser = Array.isArray(pinData?.users) && pinData.users.includes(Number(user_id));
        const isPinnedForLead = Number(pinData?.lead) === 1;
        return isPinnedForUser || isPinnedForLead;
    });    

    const pinnedIds = new Set(pinnedFeed?.map(item => item.id));

    const myTasks = organizedFeed?.filter((item) => 
        item.task === user_id && item.deadline?.done === null
    );

    const now = new Date();
    const todayNew = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const todayFeed = organizedFeed.filter((item) => {
        const created = new Date(item.creation_date);
        return (
            !pinnedIds.has(item.id) &&
            !myTasks.some(task => task.id === item.id) &&
            created >= todayNew
        );
    });

    const earlierFeed = organizedFeed.filter((item) => {
        const created = new Date(item.creation_date);
        return (
            !pinnedIds.has(item.id) &&
            !myTasks.some(task => task.id === item.id) &&
            created < todayNew
        );
    });

    return (
        <div className='activity-feed'>
            <div className='feed'>
                <div className='feed-filters'>
                    {/*<button className={`action filters-button${activeFeed === 'All' ? ' active' : ''}`} onClick={() => setActiveFeed('All')}>
                        Filter <ChevronUp size={16} />
                    </button>*/}
                    <SearchBar placeholder="Search" expanded={expanded} setExpanded={setExpanded} setSearchQuery={setSearchQuery} />
                    <button 
                        className='action alt' 
                        onClick={exportToPDF}
                        title={`Export ${activeFeed} activities to PDF`}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        <Download size={16} />
                        {!mobile && 'Export PDF'}
                    </button>
                    <div className="feed-types-container">
                        {showLeftScroll && (
                            <button 
                                className="feed-scroll-button left"
                                onClick={handleScrollLeft}
                                aria-label="Scroll left"
                            >
                                <ChevronLeft size={20} />
                            </button>
                        )}
                        <div 
                            className={`feed-types${mobile ? ' mobile' : ''}`}
                            ref={feedTypesRef}
                            onScroll={checkScrollButtons}
                        >
                            <FeedItem type="All" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem type="Notes" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem type="Emails" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem type="Faxes" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem type="Phone Calls" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem type="Texts" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem type="Tasks" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} />
                            <FeedItem
                                type="Reminders"
                                setActiveFeed={setActiveFeed}
                                activeFeed={activeFeed}
                                notifications={{
                                    reminder: reminder?.length,
                                    priority: priority?.length,
                                }}
                                mobile={mobile}
                            />
                            {/* <FeedItem type="eSignatures" setActiveFeed={setActiveFeed} activeFeed={activeFeed} mobile={mobile} /> */}
                        </div>
                        {showRightScroll && (
                            <button 
                                className="feed-scroll-button right"
                                onClick={handleScrollRight}
                                aria-label="Scroll right"
                            >
                                <ChevronRight size={20} />
                            </button>
                        )}
                    </div>
                </div>
                <AddActivity fetchFeed={fetchFeed} lead_id={lead_id} onClick={() => setAddActivity(true)} setAddActivity={setAddActivity} addActivity={addActivity} users={users} />
                {pinnedFeed?.length > 0 && (
                    <>
                        <div className="activity-feed-header subtext">
                            <span>Pinned</span>
                            <div className='divider horizontal'></div>
                            <span>{pinnedFeed.length} {pinnedFeed.length > 1 ? "items" : "item"}</span>
                        </div>
                        {pinnedFeed.map((item, index) => (
                            <Activity
                                key={`pinned-${index}`}
                                data={item}
                                user_id={user_id}
                                user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                                fetchFeed={fetchFeed}
                                reminder={reminder}
                                priority={priority}
                                setActiveItem={setActiveItem}
                                activeItem={activeItem}
                                users={users}
                                lead_id={lead_id}
                            />
                        ))}
                    </>
                )}
                {myTasks?.length > 0 && (
                    <>
                        <div className="activity-feed-header subtext">
                            <span>My Assigned Tasks</span>
                            <div className='divider horizontal'></div>
                            <span>{myTasks.length} {myTasks.length > 1 ? "items" : "item"}</span>
                        </div>
                        {myTasks.map((item, index) => (
                            <Activity
                                key={`tasks-${index}`}
                                data={item}
                                user_id={user_id}
                                user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                                fetchFeed={fetchFeed}
                                reminder={reminder}
                                priority={priority}
                                setActiveItem={setActiveItem}
                                activeItem={activeItem}
                                users={users}
                                lead_id={lead_id}
                            />
                        ))}
                    </>
                )}
                {todayFeed?.length > 0 && (
                    <>
                        <div className="activity-feed-header subtext">
                            <span>Today</span>
                            <div className='divider horizontal'></div>
                            <span>{todayFeed.length} {todayFeed.length > 1 ? "items" : "item"}</span>
                        </div>
                        {todayFeed.map((item, index) => (
                            <Activity
                                key={`today-${index}`}
                                data={item}
                                user_id={user_id}
                                user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                                fetchFeed={fetchFeed}
                                reminder={reminder}
                                priority={priority}
                                setActiveItem={setActiveItem}
                                activeItem={activeItem}
                                users={users}
                                lead_id={lead_id}
                            />
                        ))}
                    </>
                )}
                {earlierFeed?.length > 0 && (
                    <>
                        <div className="activity-feed-header subtext">
                            <span>Earlier</span>
                            <div className='divider horizontal'></div>
                            <span>{earlierFeed.length} {earlierFeed.length > 1 ? "items" : "item"}</span>
                        </div>
                        {earlierFeed.map((item, index) => (
                            <Activity
                                key={`earlier-${index}`}
                                data={item}
                                user_id={user_id}
                                user={users.find((user) => String(user.id) === String(item.task))?.name || "Unknown"}
                                fetchFeed={fetchFeed}
                                reminder={reminder}
                                priority={priority}
                                setActiveItem={setActiveItem}
                                activeItem={activeItem}
                                users={users}
                                lead_id={lead_id}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};